import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { SubmissionSummaryContext } from 'screens/Integrations/SubmissionSummary/context/SubmissionSummaryProvider';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { Button, Spinner } from '@agconnections/grow-ui';
import SavePDFIcon from 'components/Icons/components/SavePDFIcon';
import { Context } from 'components/Store';
import { IntegrationsContext } from 'screens/Integrations/context/IntegrationsProvider';
import { MccainContext } from 'screens/Integrations/MccainIntegration/context/Provider';
import { MCCAIN_PDF_WRAPPER } from 'screens/Integrations/MccainIntegration/constants';
import { INTEGRATIONS } from 'screens/Integrations/helpers/constants';
import usePDFTools from 'hooks/usePDFTools';
import useSubmissionAnalytics from 'screens/Integrations/hooks/useSubmissionAnalytics';
import { useToast } from 'components/ToastContainer';

const pdfOptions = {
  filename: `McCain_Export_${new Date()
    .toJSON()
    .slice(0, 10)
    .replaceAll('-', '_')}`,
  jsPDF: { unit: 'in', format: 'a4', orientation: 'landscape' }
};

const ModalButtons = ({ handleClose }) => {
  const [{ organization }] = useContext(Context);
  const { submitToMcCain, createMcCainPayload, isLoading } = useContext(
    IntegrationsContext
  );
  const { finishSubmission } = useContext(MccainContext);
  const {
    isLoading: isLoadingFromSubmissionContext,
    submitIntegrationWithExtraData
  } = useContext(SubmissionSummaryContext);

  const {
    releaseBeApplicationsEnable,
    releaseBeApplicationsByOrg
  } = useFlags();

  const feApplicationsActive = featureFlagRenderItem(
    releaseBeApplicationsEnable,
    releaseBeApplicationsByOrg,
    organization?.id
  );

  const { events, triggerAnalyticsEvent } = useSubmissionAnalytics();
  const { values } = useFormikContext();
  const toast = useToast();
  const { mccainDownloadPDF } = usePDFTools();

  const handleSubmitButton = async () => {
    triggerAnalyticsEvent(events.epic.Integrations.submissionRunning, {
      vendor: INTEGRATIONS.mccain
    });

    if (!feApplicationsActive) {
      const response = await submitToMcCain(values);
      if (response?.status === 201) {
        finishSubmission();
      } else {
        toast.error('Submission failed', {
          supportButton: true,
          content:
            'Unable to submit to McCain. Please try again or contact support if the problem persists.'
        });
      }
      return;
    }
    const response = await submitIntegrationWithExtraData({
      mcCainExtraData: createMcCainPayload(values)
    });

    if (response?.status === 201) {
      finishSubmission();
    } else {
      toast.error('Submission failed', {
        supportButton: true,
        content:
          'Unable to submit to McCain. Please try again or contact support if the problem persists.'
      });
    }
  };

  return (
    <>
      <div className="flex w-1/5 h-8">
        <Button
          type="outline"
          id="cancel-button"
          onClick={() => {
            handleClose();
          }}
        >
          Cancel
        </Button>
      </div>
      <div className="flex flex-row-reverse justify-start w-full">
        <div className="flex h-8">
          <Button
            type="primary"
            id="submit-button"
            onClick={handleSubmitButton}
            disabled={isLoading || isLoadingFromSubmissionContext}
          >
            {isLoading || isLoadingFromSubmissionContext ? (
              <Spinner size="xs" />
            ) : (
              'Submit to McCain'
            )}
          </Button>
        </div>
        <div className="flex h-8 px-2">
          <Button
            id="save-button"
            type="primary"
            ghost
            icon={<SavePDFIcon />}
            onClick={() => mccainDownloadPDF(MCCAIN_PDF_WRAPPER, pdfOptions)}
          >
            Save as PDF
          </Button>
        </div>
      </div>
    </>
  );
};

ModalButtons.propTypes = {
  handleClose: PropTypes.func.isRequired
};

export default ModalButtons;
